import React from 'react';
import { useNavigate } from 'react-router-dom';
import snipeLogo from '../../assets/snipe_logo.png';

function PrivacyPolicy() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#020817] text-white">
            {/* Gradient Background */}
            <div className="fixed inset-0 z-0 bg-[#020817]">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
                <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
            </div>

            {/* Content */}
            <div className="relative z-10">
                {/* Header */}
                <nav className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between border-b border-slate-800/50">
                    <button onClick={() => navigate('/')} className="cursor-pointer">
                        <img src={snipeLogo} alt="Snipe Logo" className="h-16 sm:h-20 w-auto object-contain" />
                    </button>
                    <button 
                        onClick={() => navigate('/')}
                        className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-semibold text-white hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                    >
                        Back to Home
                    </button>
                </nav>

                {/* Privacy Policy Content */}
                <div className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl text-left">
                    <div className="bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl border border-slate-800/50 p-6 sm:p-10 text-left">
                        <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-white text-left">
                            Privacy Policy
                        </h1>

                        {/* Header Info */}
                        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50 mb-6 text-left">
                            <p className="text-xs sm:text-sm mb-1.5 text-slate-400 text-left"><strong className="text-slate-300">Last updated:</strong> 16/01/2026</p>
                            <p className="text-xs sm:text-sm mb-1.5 text-slate-400 text-left"><strong className="text-slate-300">Business:</strong> SNIPEAIO PTY LTD (ABN 29 685 306 978 | ACN 685 306 978)</p>
                            <p className="text-xs sm:text-sm text-slate-400 text-left"><strong className="text-slate-300">Privacy contact:</strong> <a href="mailto:info@snipeaio.com" className="text-emerald-400 hover:text-emerald-300 underline">info@snipeaio.com</a></p>
                        </div>

                        <p className="text-sm leading-relaxed mb-6 text-slate-300 text-left">
                            We handle personal information in line with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
                        </p>

                        <div className="space-y-6 text-slate-300 text-left">
                            {/* Section 1 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">1. What we collect</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">We may collect:</p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm leading-relaxed ml-4 text-left">
                                    <li><strong>account details:</strong> name, email, password (encrypted/hashed)</li>
                                    <li><strong>identifiers:</strong> Discord username/ID, role status, subscription status</li>
                                    <li><strong>billing details:</strong> payment status and transaction references (we don't store full card details)</li>
                                    <li><strong>usage data:</strong> pages viewed, feature usage, device/browser info, IP address, timestamps</li>
                                    <li>support messages and moderation reports</li>
                                    <li>marketing preferences</li>
                                </ul>
                            </section>

                            {/* Section 2 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">2. How we collect it</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">We collect personal information when you:</p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm leading-relaxed ml-4 text-left">
                                    <li>create an account, start a trial, or subscribe</li>
                                    <li>connect your Discord account for role gating</li>
                                    <li>contact support</li>
                                    <li>use our website/app (cookies and similar tech)</li>
                                </ul>
                            </section>

                            {/* Section 3 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">3. Why we collect it</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">We use personal information to:</p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm leading-relaxed ml-4 text-left">
                                    <li>provide and operate the Service</li>
                                    <li>manage access and role gating</li>
                                    <li>process subscriptions and prevent fraud/abuse</li>
                                    <li>provide support and communicate service updates</li>
                                    <li>improve features and reliability</li>
                                    <li>send marketing where permitted (opt out available)</li>
                                </ul>
                            </section>

                            {/* Section 4 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">4. Who we share it with</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">
                                    We may share personal information with service providers that help us operate the Service, such as:
                                </p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm leading-relaxed ml-4 mb-3 text-left">
                                    <li>payment providers</li>
                                    <li>hosting and infrastructure providers</li>
                                    <li>analytics/logging providers</li>
                                    <li>Discord (for role/access sync)</li>
                                    <li>contractors supporting development and support</li>
                                </ul>
                                <p className="text-sm leading-relaxed text-left">
                                    We may also disclose information if required by law or a regulator.
                                </p>
                            </section>

                            {/* Section 5 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">5. Overseas storage</h2>
                                <p className="text-sm leading-relaxed text-left">
                                    Some providers may store or process data outside Australia. We take reasonable steps to use reputable providers and protect your information.
                                </p>
                            </section>

                            {/* Section 6 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">6. Cookies and analytics</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">We use cookies and similar technologies to:</p>
                                <ul className="list-disc list-inside space-y-1.5 text-sm leading-relaxed ml-4 mb-3 text-left">
                                    <li>keep you logged in</li>
                                    <li>remember settings</li>
                                    <li>measure usage and improve the Service</li>
                                </ul>
                                <p className="text-sm leading-relaxed text-left">
                                    You can control cookies in your browser, but parts of the Service may not work if cookies are disabled.
                                </p>
                            </section>

                            {/* Section 7 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">7. Security and retention</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">
                                    We use reasonable security controls to protect information. No system is perfectly secure.
                                </p>
                                <p className="text-sm leading-relaxed text-left">
                                    We keep information only as long as needed for the purposes above (or as required by law).
                                </p>
                            </section>

                            {/* Section 8 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">8. Access, correction, and deletion</h2>
                                <p className="text-sm leading-relaxed mb-2 text-left">
                                    You can request access to or correction of your personal information by emailing <a href="mailto:info@snipeaio.com" className="text-emerald-400 hover:text-emerald-300">info@snipeaio.com</a>.
                                </p>
                                <p className="text-sm leading-relaxed text-left">
                                    If you ask us to delete data, we'll do so where we can, unless we need to keep it for legal, security, or operational reasons.
                                </p>
                            </section>

                            {/* Section 9 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">9. Data breaches</h2>
                                <p className="text-sm leading-relaxed text-left">
                                    If a data breach occurs that is likely to result in serious harm, we will take reasonable steps to comply with any applicable notification requirements.
                                </p>
                            </section>

                            {/* Section 10 */}
                            <section className="text-left">
                                <h2 className="text-base sm:text-lg font-semibold mb-3 text-white text-left">10. Complaints</h2>
                                <p className="text-sm leading-relaxed text-left">
                                    If you have a privacy complaint, email <a href="mailto:info@snipeaio.com" className="text-emerald-400 hover:text-emerald-300">info@snipeaio.com</a>. If we can't resolve it, you can contact the OAIC.
                                </p>
                            </section>
                        </div>
                    </div>

                    {/* Back to Home Button at bottom */}
                    <div className="text-center mt-8 sm:mt-12">
                        <button
                            onClick={() => navigate('/')}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-3 rounded-lg font-semibold text-white hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                        >
                            Back to Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicy;

